/*
 * File: rcnir-delivery-request.js
 * Version: v1.0.0 (2026-07-18)
 *
 * 日本国内のカートで、お届け希望日・時間帯をShopify cart attributesへ保存する。
 */

(() => {
  'use strict';

  const DATE_ATTRIBUTE_KEY = 'お届け希望日';
  const TIME_ATTRIBUTE_KEY = 'お届け時間帯';
  const FASTEST_VALUE = '最短';
  const ALLOWED_TIME_VALUES = new Set([
    '',
    '午前中',
    '14:00–16:00',
    '16:00–18:00',
    '18:00–20:00',
    '19:00–21:00'
  ]);

  const rootUrl = window.Shopify?.routes?.root || '/';
  const cartUpdateUrl = `${rootUrl}cart/update.js`;

  const pad2 = (value) => String(value).padStart(2, '0');

  const formatIsoDate = (date) => (
    `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`
  );

  const getTokyoDateWindow = (now = new Date()) => {
    const formatter = new Intl.DateTimeFormat('en-US-u-ca-gregory', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    });

    const parts = {};
    formatter.formatToParts(now).forEach((part) => {
      if (part.type !== 'literal') {
        parts[part.type] = Number(part.value);
      }
    });

    const baseDate = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    const minimumOffset = parts.hour >= 12 ? 6 : 5;
    const minimumDate = new Date(baseDate.getTime());
    const maximumDate = new Date(baseDate.getTime());

    minimumDate.setUTCDate(minimumDate.getUTCDate() + minimumOffset);
    maximumDate.setUTCDate(maximumDate.getUTCDate() + 14);

    return {
      minimum: formatIsoDate(minimumDate),
      maximum: formatIsoDate(maximumDate)
    };
  };

  const isStrictIsoDate = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return false;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));

    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  };

  class DeliveryRequestManager {
    constructor() {
      this.components = new Set();
      this.initialized = false;
      this.state = {
        mode: 'fastest',
        date: '',
        time: ''
      };
      this.saveQueue = Promise.resolve();
      this.latestSaveId = 0;
      this.checkoutInProgress = false;

      document.addEventListener('submit', (event) => this.handleCartSubmit(event), true);
      window.setInterval(() => this.refreshDateConstraints(), 60 * 1000);
    }

    register(component) {
      this.components.add(component);

      if (!this.initialized) {
        const initialDate = component.dataset.initialDate || FASTEST_VALUE;
        const initialTime = component.dataset.initialTime || '';

        this.state.mode = initialDate !== FASTEST_VALUE && initialDate !== ''
          ? 'date'
          : 'fastest';
        this.state.date = this.state.mode === 'date' ? initialDate : '';
        this.state.time = ALLOWED_TIME_VALUES.has(initialTime) ? initialTime : '';
        this.initialized = true;
      }

      component.render(this.state, getTokyoDateWindow());
    }

    unregister(component) {
      this.components.delete(component);
    }

    updateFromComponent(nextState) {
      this.state = {
        mode: nextState.mode === 'date' ? 'date' : 'fastest',
        date: nextState.mode === 'date' ? nextState.date : '',
        time: ALLOWED_TIME_VALUES.has(nextState.time) ? nextState.time : ''
      };

      this.clearError();
      this.renderAll();
      this.enqueueSave();
    }

    renderAll() {
      const dateWindow = getTokyoDateWindow();
      this.components.forEach((component) => component.render(this.state, dateWindow));
    }

    refreshDateConstraints() {
      this.renderAll();
    }

    buildAttributes() {
      return {
        [DATE_ATTRIBUTE_KEY]: this.state.mode === 'fastest'
          ? FASTEST_VALUE
          : this.state.date,
        [TIME_ATTRIBUTE_KEY]: this.state.time
      };
    }

    enqueueSave() {
      const saveId = ++this.latestSaveId;
      const attributes = this.buildAttributes();

      this.setBusy(true);

      const saveOperation = this.saveQueue
        .catch(() => undefined)
        .then(() => this.saveAttributes(attributes));

      this.saveQueue = saveOperation;

      saveOperation
        .then(() => {
          if (saveId === this.latestSaveId) {
            this.clearError();
            this.setBusy(false);
          }
        })
        .catch(() => {
          if (saveId === this.latestSaveId) {
            this.showError(this.getMessage('saveError'));
            this.setBusy(false);
          }
        });

      return saveOperation;
    }

    async saveAttributes(attributes) {
      const response = await fetch(cartUpdateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ attributes })
      });

      if (!response.ok) {
        let reason = `HTTP ${response.status}`;

        try {
          const payload = await response.json();
          reason = payload.description || payload.message || reason;
        } catch (error) {
          // ShopifyがJSON以外を返した場合はHTTPステータスを使用する。
        }

        console.error('ERROR cart attribute save failed', reason);
        throw new Error(reason);
      }

      return response.json();
    }

    validate() {
      if (this.state.mode === 'fastest') {
        return { valid: true, message: '' };
      }

      if (!this.state.date) {
        return {
          valid: false,
          message: this.getMessage('dateRequiredError')
        };
      }

      const dateWindow = getTokyoDateWindow();
      const validDate = (
        isStrictIsoDate(this.state.date) &&
        this.state.date >= dateWindow.minimum &&
        this.state.date <= dateWindow.maximum
      );

      if (!validDate) {
        return {
          valid: false,
          message: this.getMessage('dateRangeError')
        };
      }

      return { valid: true, message: '' };
    }

    async handleCartSubmit(event) {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('.cart-page, .cart-drawer__footer')) return;

      const submitter = event.submitter;
      if (submitter && submitter.name !== 'checkout') return;

      event.preventDefault();

      if (this.checkoutInProgress) return;

      const validation = this.validate();

      if (!validation.valid) {
        this.showError(validation.message);
        this.focusVisibleComponent();
        return;
      }

      this.checkoutInProgress = true;
      this.setCheckoutButtonsDisabled(true);

      try {
        await this.enqueueSave();

        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'checkout';
        form.appendChild(checkoutInput);

        HTMLFormElement.prototype.submit.call(form);
      } catch (error) {
        this.checkoutInProgress = false;
        this.setCheckoutButtonsDisabled(false);
        this.showError(this.getMessage('saveError'));
        this.focusVisibleComponent();
      }
    }

    setBusy(isBusy) {
      this.components.forEach((component) => {
        if (isBusy) {
          component.setAttribute('aria-busy', 'true');
        } else {
          component.removeAttribute('aria-busy');
        }
      });
    }

    setCheckoutButtonsDisabled(disabled) {
      document
        .querySelectorAll('.cart-page [name="checkout"], .cart-drawer__footer [name="checkout"]')
        .forEach((button) => {
          button.disabled = disabled;
          button.setAttribute('aria-busy', disabled ? 'true' : 'false');
        });
    }

    showError(message) {
      this.components.forEach((component) => component.showError(message));
    }

    clearError() {
      this.components.forEach((component) => component.clearError());
    }

    focusVisibleComponent() {
      const visibleComponent = Array.from(this.components).find(
        (component) => component.getClientRects().length > 0
      );

      visibleComponent?.focusInvalidField(this.state);
    }

    getMessage(key) {
      const component = Array.from(this.components)[0];
      return component?.dataset?.[key] || '';
    }
  }

  const manager = new DeliveryRequestManager();

  class RcnirDeliveryRequest extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      this.modeInputs = Array.from(this.querySelectorAll('input[type="radio"]'));
      this.dateInput = this.querySelector('.rcnir-delivery-request__date');
      this.timeSelect = this.querySelector('.rcnir-delivery-request__time');
      this.errorElement = this.querySelector('.rcnir-delivery-request__error');

      this.modeInputs.forEach((input) => {
        input.addEventListener('change', () => this.handleChange(), {
          signal: this.abortController.signal
        });
      });

      this.dateInput?.addEventListener('change', () => this.handleChange(), {
        signal: this.abortController.signal
      });

      this.timeSelect?.addEventListener('change', () => this.handleChange(), {
        signal: this.abortController.signal
      });

      manager.register(this);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      manager.unregister(this);
    }

    handleChange() {
      const mode = this.querySelector('input[type="radio"]:checked')?.value || 'fastest';

      manager.updateFromComponent({
        mode,
        date: mode === 'date' ? this.dateInput?.value || '' : '',
        time: this.timeSelect?.value || ''
      });
    }

    render(state, dateWindow) {
      this.modeInputs.forEach((input) => {
        input.checked = input.value === state.mode;
      });

      if (this.dateInput) {
        this.dateInput.min = dateWindow.minimum;
        this.dateInput.max = dateWindow.maximum;
        this.dateInput.disabled = state.mode !== 'date';
        this.dateInput.hidden = state.mode !== 'date';
        this.dateInput.value = state.mode === 'date' ? state.date : '';
      }

      if (this.timeSelect) {
        this.timeSelect.value = state.time;
      }
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    focusInvalidField(state) {
      if (state.mode === 'date') {
        this.dateInput?.focus();
      } else {
        this.timeSelect?.focus();
      }
    }
  }

  if (!window.customElements.get('rcnir-delivery-request')) {
    window.customElements.define('rcnir-delivery-request', RcnirDeliveryRequest);
  }
})();
